import { useQuery } from '@tanstack/react-query';
import { useAppConstantsContext } from '@/context/AppConstants';
import {
  PropertyOptionsLocationParams,
  SerializedPropertyOptions,
} from '@/search/useSearchPropertyOptions/interfaces';
import { PropertyOptionsTeaser } from '@/search/useSearchPropertyOptions/serializer/PropertyOptionsTeaser';
import { Nullable } from '@/utils/helperTypes';

export const V2_PROPERTY_OPTIONS_ENDPOINT = '/ms/v2/property/options';

export type PropertyOptionsSearchParams = {
  name: string;
  location: PropertyOptionsLocationParams | string;
} & Nullable<{
  // DD filters
  property_min_bathrooms?: number;
  property_max_bathrooms?: number;
  property_min_bedrooms?: number;
  property_max_bedrooms?: number;
  property_min_last_sale_date?: string;
  property_max_last_sale_date?: string;
  property_owner_occupied?: boolean;
  property_min_last_sale_price?: number;
  property_max_last_sale_price?: number;

  // Listings page filters
  search_type?: string;
  bedrooms?: number;
  bathrooms?: number;
  min_price?: number;
  max_price?: number;
  city?: string;
  state?: string;
  county?: string;
}>;

async function searchPropertyOptionsResults(
  queryParams: PropertyOptionsSearchParams,
  brandLink: string
) {
  brandLink = brandLink ? `${brandLink}` : '';

  try {
    const headers = new Headers({
      'content-type': 'application/x-www-form-urlencoded',
    });

    const response = await fetch(
      `${brandLink}${V2_PROPERTY_OPTIONS_ENDPOINT}?${generateQueryString(
        queryParams
      )}`,
      {
        method: 'GET',
        headers,
      }
    );

    if (response.ok) {
      return response.json();
    }
    throw new Error(`An unexpected error occurred. ${response}`);
  } catch (error) {
    throw new Error(`An unexpected error occurred. ${error}`);
  }
}

export function generateQueryString(
  searchQueryParamsObj: PropertyOptionsSearchParams
): string {
  return Object.keys(searchQueryParamsObj)
    .map((paramName: string) => {
      const value =
        searchQueryParamsObj[paramName as keyof typeof searchQueryParamsObj];

      if (paramName === 'location') {
        const { city, state, county } = value as PropertyOptionsLocationParams;
        if (state) {
          if (city) {
            return `${paramName}=${encodeURIComponent(`${city}, ${state}`)}`;
          }

          if (county) {
            return `${paramName}=${encodeURIComponent(`${county}, ${state}`)}`;
          }

          return `${paramName}=${state}`;
        }
      }

      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        if (value) return `${paramName}=${encodeURIComponent(value)}`;
      }
    })
    .filter(Boolean)
    .join('&');
}

export function useSearchPropertyOptions(
  propertyOptionsProps: PropertyOptionsSearchParams,
  enabled = true
) {
  const {
    links: { brandLink },
  } = useAppConstantsContext();

  return useQuery<SerializedPropertyOptions, Error>({
    queryKey: ['searchPropertyOptionsResult', propertyOptionsProps],
    queryFn: async () => {
      const searchPropertyOptionsResult = await searchPropertyOptionsResults(
        propertyOptionsProps,
        brandLink
      );

      return new PropertyOptionsTeaser(searchPropertyOptionsResult).data;
    },

    enabled: !!enabled,
  });
}
